<script setup lang="ts">

withDefaults(defineProps<{
  secret?: boolean
  modelValue?: string
  length: number
  size?: 'small' | 'large'
  isOTP?: boolean
}>(), { secret: false,size: 'large',isOTP: false, })

const values = computed(() => Array.from({ length: __props.length }, (_, index) => __props.modelValue?.charAt(index) ?? ''))

const inputs = useTemplateRef('individual-inputs')

function keyUp (event: KeyboardEvent, index: number) {
  if ((event.key === 'Backspace' || event.key === 'Delete')) {
    emit('update:modelValue', __props.modelValue?.slice(0, -1) || '')
  }
  emit('keyup', event)
}

const emit = defineEmits<{
  'update:modelValue': [value: string]
  keydown: [value: KeyboardEvent]
  keyup: [value: KeyboardEvent]
}>()

defineExpose({
  focus: () => {
    inputs.value?.[(__props.modelValue?.length || 1) - 1]?.focus()
  },
})

watch(() => __props.modelValue, (newVal) => {
  const indexToFocus = newVal ? newVal.length : 0
  if (indexToFocus < __props.length) {
    inputs.value?.[indexToFocus]?.focus()
  }
})

function onPaste (payload: ClipboardEvent) {
  // Handle paste event if needed
  payload.preventDefault()
  const code = payload.clipboardData?.getData('text')
  if (code && /^\d+$/.test(code)) {
    emit('update:modelValue', code)
  }
}

function onInput (event: Event, index: number) {
  const input = event.target as HTMLInputElement
  if (__props.modelValue) {
    const newValue = __props.modelValue?.slice(0, index) + input.value + __props.modelValue?.slice(index + 1)
    emit('update:modelValue', newValue)
  }
  else {
    emit('update:modelValue', input.value)
  }
}
</script>

<template>
  <div class="flex gap-1.5 xs:gap-2 g-input-field" :class="[`g-input-${size}`]">
    <input
      v-for="(value, index) in values"
      :key="index"
      ref="individual-inputs"
      v-mask="'#'"
      :autocomplete="isOTP && index == 0 ? 'one-time-code' : 'off'"
      :class="{'secret': secret, [`g-inner-input-${index}`]: true }"
      :name="`pin-input-${index}`"
      :type="'text'"
      size="1"
      :value="value"
      class="
        text-center border-solid border-1 rounded-full min-w-0
        border-grey-primary focus:outline-v2-active g-inner-input
      "
      maxlength="6"
      pattern="\d*"
      inputmode="numeric"
      @keyup="(event) => keyUp(event, index)"
      @keydown="(event) => emit('keydown', event)"
      @paste="onPaste"
      @input="(event) => onInput(event, index)"
    >
  </div>
</template>

<style scoped>
/*
  Hide input characters when secret is true

  Ideally we should do this with the input type=password, but chrome
  is a bit annoying when doing this and keeps prompting to use one of the saved
  passwords when one of the inputs is active.

  This is a workaround to have the same effect without using that type.

  Note that the autocomplete when set "one-time-code" should have the same effect
  but it is not supported in all browsers yet.
*/
input.secret {
  -webkit-text-security: disc;

  /* For future compatibility */
  /* stylelint-disable property-no-unknown */
  text-security: disc;
  /* stylelint-enable property-no-unknown */
}

.g-input-small {
  .g-inner-input {
    @apply text-sm w-10 h-10 p-3;
  }
}

.g-input-large {
  .g-inner-input {
    @apply text-md w-11 h-11 p-3;
  }
}

@media (min-width: 440px) {
  .g-input-large .g-inner-input {
    @apply w-14 h-14 p-4;
  }
}
</style>
